import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatMoney, type Application, type ApplicationStatusValue } from '@teentack/shared';
import { useMyJobs } from '@/features/jobs/use-jobs';
import { useDecideApplication, useJobApplications } from '@/features/applications/use-applications';
import { useCreateAssignments } from '@/features/assignments/use-assignments';
import { PosterShell } from '@/features/poster/PosterShell';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorNote, InfoNote } from '@/components/Form';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { SelectInput } from '@/components/ui/Input';
import { MapPinIcon, UsersIcon } from '@/components/ui/Icons';
import { MessageButton } from '@/features/messaging/MessageButton';
import { JobBidsList } from '@/features/bids/JobBidsList';
import { ReportButton } from '@/features/safety/ReportButton';
import { FavouriteButton } from '@/features/favourites/FavouriteButton';
import { ReputationBadge } from '@/features/reviews/Stars';
import { useReputations } from '@/features/reviews/use-reviews';
import { presentApiError } from '@/lib/form-errors';

const STATUS_TONES: Record<ApplicationStatusValue, BadgeTone> = {
  SUBMITTED: 'brand',
  SHORTLISTED: 'accent',
  ACCEPTED: 'safe',
  DECLINED: 'neutral',
};

function label(status: string): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

/**
 * The job poster's review desk. [Job Poster steps 11-18]
 *
 * Pick a job, compare who applied, shortlist the ones worth a look, then assign.
 * Assigning goes through the idempotent mutation, so a double-click cannot book
 * the same teen twice or overrun the headcount.
 */
export function ApplicantsPage() {
  const jobs = useMyJobs({ pageSize: 100 });

  // Links from the console and the job list arrive as ?jobId= / ?status=.
  // Reading them is what makes "Review applicants" open the right job rather
  // than dumping the reader on "All my jobs".
  const [params, setParams] = useSearchParams();
  const jobId = params.get('jobId') ?? '';
  const status = (params.get('status') ?? '') as ApplicationStatusValue | '';
  // Applications and bids are two answers to the same job, so they live on one
  // screen behind a tab rather than in two places an employer has to remember.
  const tab = params.get('tab') === 'bids' ? 'bids' : 'applications';

  function setTab(next: 'applications' | 'bids'): void {
    const updated = new URLSearchParams(params);
    if (next === 'bids') updated.set('tab', 'bids');
    else updated.delete('tab');
    setParams(updated, { replace: true });
  }

  function setJobId(next: string): void {
    const updated = new URLSearchParams(params);
    if (next) updated.set('jobId', next);
    else updated.delete('jobId');
    setParams(updated, { replace: true });
  }

  function setStatus(next: ApplicationStatusValue | ''): void {
    const updated = new URLSearchParams(params);
    if (next) updated.set('status', next);
    else updated.delete('status');
    setParams(updated, { replace: true });
  }

  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /* --- Shortlisting filters [Job Poster step 13] --------------------------
   * In the query string like the others, so a narrowed list is a link an
   * employer can keep, reload, or send to whoever else is deciding.
   */
  const minRating = params.get('minRating') ?? '';
  const skillIds = params.getAll('skillIds');
  const hasAvailability = params.get('hasAvailability') === 'true';

  function setFilter(key: string, next: string): void {
    const updated = new URLSearchParams(params);
    if (next) updated.set(key, next);
    else updated.delete(key);
    setParams(updated, { replace: true });
    setSelected([]);
  }

  function toggleSkill(skillId: string): void {
    const updated = new URLSearchParams(params);
    const current = updated.getAll('skillIds');
    updated.delete('skillIds');
    const next = current.includes(skillId)
      ? current.filter((id) => id !== skillId)
      : [...current, skillId];
    for (const id of next) updated.append('skillIds', id);
    setParams(updated, { replace: true });
    setSelected([]);
  }

  const filtersOn = minRating !== '' || skillIds.length > 0 || hasAvailability;

  function clearFilters(): void {
    const updated = new URLSearchParams(params);
    for (const key of ['minRating', 'skillIds', 'hasAvailability']) updated.delete(key);
    setParams(updated, { replace: true });
  }

  const applications = useJobApplications(
    {
      ...(jobId ? { jobId } : {}),
      ...(status ? { status } : {}),
      ...(minRating ? { minRating: Number(minRating) } : {}),
      ...(skillIds.length > 0 ? { skillIds } : {}),
      ...(hasAvailability ? { hasAvailability: true } : {}),
      pageSize: 50,
    },
    true,
  );
  const decide = useDecideApplication();
  const assign = useCreateAssignments();

  // One request for every applicant on the page, rather than one each.
  const applicantIds = (applications.data?.items ?? []).map(
    (application) => application.teen.id,
  );
  const reputations = useReputations(applicantIds);

  const activeJob = jobs.data?.items.find((job) => job.id === jobId);
  const slotsLeft = activeJob ? activeJob.teensNeeded - activeJob.teensAssigned : 0;

  function toggleSelected(teenId: string): void {
    setSelected((current) =>
      current.includes(teenId) ? current.filter((id) => id !== teenId) : [...current, teenId],
    );
  }

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
    } catch (caught) {
      setError(presentApiError(caught, 'Something went wrong').message);
    }
  }

  const page = applications.data;

  return (
    <PosterShell
      title="Applicants"
      description="Every teen here is verified and, if under 18, already has guardian approval for this job. Shortlist the ones you want to talk to, then assign."
    >
      <div className="mb-5 flex gap-2 border-b border-line">
        {(['applications', 'bids'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            aria-current={tab === value ? 'page' : undefined}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
              tab === value
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-ink'
            }`}
          >
            {value === 'applications' ? 'Applications' : 'Bids'}
          </button>
        ))}
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2">
        <SelectInput
          label="Job"
          value={jobId}
          onChange={(event) => {
            setJobId(event.target.value);
            setSelected([]);
          }}
        >
          <option value="">All my jobs</option>
          {jobs.data?.items.map((job) => (
            <option key={job.id} value={job.id}>
              {job.title} · {job.teensAssigned}/{job.teensNeeded} filled
            </option>
          ))}
        </SelectInput>

        {tab === 'applications' && (
        <SelectInput
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.target.value as ApplicationStatusValue | '')}
          optional
        >
          <option value="">Any status</option>
          {(['SUBMITTED', 'SHORTLISTED', 'ACCEPTED', 'DECLINED'] as const).map((value) => (
            <option key={value} value={value}>
              {label(value)}
            </option>
          ))}
        </SelectInput>
        )}
      </div>

      {/* Narrowing the list down to the people worth comparing.
          [Job Poster step 13] */}
      {tab === 'applications' && (
        <div className="mb-6 rounded-2xl border border-line bg-white p-4 shadow-soft">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-bold tracking-[0.08em] text-slate-500 uppercase">
              Narrow it down
            </p>
            {filtersOn && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <SelectInput
              label="Rated at least"
              value={minRating}
              onChange={(event) => setFilter('minRating', event.target.value)}
              hint="Teens nobody has reviewed yet are left out when this is set."
              optional
            >
              <option value="">Any rating</option>
              {[3, 3.5, 4, 4.5].map((value) => (
                <option key={value} value={value}>
                  {value} stars and up
                </option>
              ))}
            </SelectInput>

            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
                <input
                  type="checkbox"
                  checked={hasAvailability}
                  onChange={(event) =>
                    setFilter('hasAvailability', event.target.checked ? 'true' : '')
                  }
                  className="size-4 rounded border-slate-300 text-brand-600"
                />
                Has told us when they can work
              </label>
            </div>
          </div>

          {/* Skills come from the job being reviewed: filtering by a skill the
              job never asked for is a question nobody has. */}
          {activeJob && activeJob.skills.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-ink">Skills this job asks for</p>
              <p className="mt-0.5 text-xs text-slate-500">
                Picking more than one narrows to teens who have all of them.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {activeJob.skills.map((skill) => {
                  const on = skillIds.includes(skill.publicId);
                  return (
                    <button
                      key={skill.publicId}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleSkill(skill.publicId)}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition duration-200
                        focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
                          on
                            ? 'bg-ink text-white'
                            : 'border border-line bg-white text-slate-600 hover:border-brand-300'
                        }`}
                    >
                      {skill.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'bids' && <JobBidsList jobId={jobId} />}

      {tab === 'applications' && (
      <>
      <div className="mb-4 space-y-3">
        <ErrorNote message={error} />
        {notice && <InfoNote>{notice}</InfoNote>}
      </div>

      {/* Assign bar — only meaningful once a job is chosen. */}
      {jobId && selected.length > 0 && (
        <div className="sticky top-20 z-10 mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50/90 p-4 backdrop-blur">
          <UsersIcon className="size-5 text-brand-700" />
          <p className="flex-1 text-sm font-semibold text-brand-800">
            {selected.length} selected · {slotsLeft} place{slotsLeft === 1 ? '' : 's'} left on this
            job
          </p>
          <Button
            disabled={assign.isPending || selected.length > slotsLeft}
            onClick={() =>
              void run(async () => {
                await assign.mutateAsync({ jobId, teenIds: selected });
                setSelected([]);
              }, 'Assigned. The teen will confirm from their side.')
            }
          >
            {assign.isPending ? 'Assigning…' : `Assign ${selected.length}`}
          </Button>
          <Button variant="ghost" onClick={() => setSelected([])} disabled={assign.isPending}>
            Clear
          </Button>
        </div>
      )}

      {selected.length > slotsLeft && jobId && (
        <p className="mb-4 rounded-xl border border-sun-200 bg-sun-50 px-4 py-3 text-sm font-medium text-sun-700">
          This job only has {slotsLeft} place{slotsLeft === 1 ? '' : 's'} left. Deselect{' '}
          {selected.length - slotsLeft} to continue.
        </p>
      )}

      {applications.isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      )}

      {page && page.items.length === 0 && (
        <EmptyState icon={<UsersIcon className="size-6" />} title="No applications yet">
          {jobId
            ? 'Nobody has applied to this job yet. Applications appear here as they arrive.'
            : 'Applications appear here as teens apply to your jobs.'}
        </EmptyState>
      )}

      {page && page.items.length > 0 && (
        <ul className="space-y-4">
          {page.items.map((application: Application) => {
            const isSelected = selected.includes(application.teen.id);
            const assignable =
              Boolean(jobId) && !application.assigned && application.status !== 'DECLINED';

            return (
              <li
                key={application.id}
                className={`rounded-2xl border bg-white p-5 shadow-soft transition sm:p-6 ${
                  isSelected ? 'border-brand-500 ring-1 ring-brand-500/20' : 'border-line'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    {assignable && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelected(application.teen.id)}
                        aria-label={`Select ${application.teen.name}`}
                        className="mt-1 size-4 rounded border-slate-300 text-brand-600"
                      />
                    )}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <h2 className="text-base font-bold text-ink">{application.teen.name}</h2>
                        <ReputationBadge
                          reputation={reputations.data?.[application.teen.id]}
                        />
                      </div>
                      <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        <span>{application.job.title}</span>
                        {application.teen.city && (
                          <span className="inline-flex items-center gap-1">
                            <MapPinIcon className="size-3.5 text-slate-400" />
                            {[application.teen.city, application.teen.state]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        )}
                        <span className="font-semibold text-ink">
                          {formatMoney(
                            application.job.pay.amountMinor,
                            application.job.pay.currency,
                          )}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={STATUS_TONES[application.status]}>
                      {label(application.status)}
                    </Badge>
                    {application.teen.isMinor && application.consentStatus === 'APPROVED' && (
                      <Badge tone="safe">Guardian approved</Badge>
                    )}
                    {application.assigned && <Badge tone="accent">Assigned</Badge>}
                  </div>
                </div>

                {application.coverNote && (
                  <p className="mt-4 rounded-xl bg-canvas px-4 py-3 text-sm leading-relaxed text-slate-600">
                    “{application.coverNote}”
                  </p>
                )}

                {application.teen.skills.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {application.teen.skills.slice(0, 6).map((skill) => (
                      <Badge key={skill} tone="neutral">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
                  {/* Asking a question should not require a decision first. */}
                  <MessageButton
                    jobId={application.job.id}
                    personId={application.teen.id}
                    label="Message"
                  />
                  {/* Only meaningful once they have taken the job. */}
                  {application.assigned && (
                    <FavouriteButton
                      teenId={application.teen.id}
                      teenName={application.teen.name}
                    />
                  )}
                  <ReportButton
                    personId={application.teen.id}
                    personName={application.teen.name}
                    jobId={application.job.id}
                  />
                </div>

                {application.status !== 'ACCEPTED' && (
                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="outline"
                      disabled={decide.isPending || application.status === 'SHORTLISTED'}
                      onClick={() =>
                        void run(
                          () =>
                            decide.mutateAsync({
                              applicationId: application.id,
                              decision: 'SHORTLIST',
                            }),
                          `${application.teen.name} shortlisted.`,
                        )
                      }
                    >
                      Shortlist
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={decide.isPending || application.status === 'DECLINED'}
                      onClick={() =>
                        void run(
                          () =>
                            decide.mutateAsync({
                              applicationId: application.id,
                              decision: 'DECLINE',
                            }),
                          `${application.teen.name} declined.`,
                        )
                      }
                    >
                      Decline
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!jobId && page && page.items.length > 0 && (
        <p className="mt-6 text-center text-xs text-slate-400">
          Choose a single job above to select and assign teens.
        </p>
      )}
      </>
      )}
    </PosterShell>
  );
}
